import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@/app/store";
import { APIResponse } from "@/types/custom-http";
import { SettingsMap, SystemSetting, SystemSettingsState } from "./systems-settings.interface";
import * as thunks from "./systems-settingsThunks";

const buildSettingsMap = (settings: SystemSetting[]): SettingsMap => {
  const nextMap: SettingsMap = {};

  settings.forEach((setting) => {
    if (!nextMap[setting.section]) {
      nextMap[setting.section] = {};
    }
    nextMap[setting.section][setting.key] = setting;
  });

  return nextMap;
};

const flattenSettingsMap = (settingsMap: SettingsMap): SystemSetting[] =>
  Object.values(settingsMap).flatMap((sectionMap) => Object.values(sectionMap));

const upsertSetting = (settingsMap: SettingsMap, setting: SystemSetting): SettingsMap => ({
  ...settingsMap,
  [setting.section]: {
    ...(settingsMap[setting.section] ?? {}),
    [setting.key]: setting,
  },
});

const removeSetting = (settingsMap: SettingsMap, setting: SystemSetting): SettingsMap => {
  const sectionMap = settingsMap[setting.section];
  if (!sectionMap || !sectionMap[setting.key]) {
    return settingsMap;
  }

  const { [setting.key]: _deletedSetting, ...restSectionMap } = sectionMap;

  if (Object.keys(restSectionMap).length === 0) {
    const { [setting.section]: _deletedSection, ...restSettingsMap } = settingsMap;
    return restSettingsMap;
  }

  return {
    ...settingsMap,
    [setting.section]: restSectionMap,
  };
};

const initialState: SystemSettingsState = {
  bySection: {},
  selected: null,
  loading: false,
  error: null,
};

const systemSettingsSlice = createSlice({
  name: "systemSettings",
  initialState,
  reducers: {
    clearSystemSettings: (state) => {
      state.bySection = {};
    },
    replaceSystemSettings: (state, action: PayloadAction<SystemSetting[]>) => {
      state.bySection = buildSettingsMap(action.payload);
      state.error = null;
    },
    clearSelectedSystemSetting: (state) => {
      state.selected = null;
    },
    clearSystemSettingsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(thunks.fetchSystemSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchSystemSettings.fulfilled, (state, action: PayloadAction<APIResponse<SystemSetting[]>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.bySection = buildSettingsMap(action.payload.data);
        }
      })
      .addCase(thunks.fetchSystemSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.fetchSystemSettingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchSystemSettingById.fulfilled, (state, action: PayloadAction<APIResponse<SystemSetting>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.selected = action.payload.data;
          state.bySection = upsertSetting(state.bySection, action.payload.data);
        }
      })
      .addCase(thunks.fetchSystemSettingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.fetchSystemSettingsBySection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchSystemSettingsBySection.fulfilled, (state, action: PayloadAction<APIResponse<SystemSetting[]>>) => {
        state.loading = false;
        if (action.payload.data) {
          const sectionSettings = action.payload.data;
          if (sectionSettings.length === 0) {
            return;
          }

          const nextSectionMap: Record<string, SystemSetting> = {};
          sectionSettings.forEach((setting) => {
            nextSectionMap[setting.key] = setting;
          });

          state.bySection = {
            ...state.bySection,
            [sectionSettings[0].section]: nextSectionMap,
          };
        }
      })
      .addCase(thunks.fetchSystemSettingsBySection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.createSystemSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.createSystemSetting.fulfilled, (state, action: PayloadAction<APIResponse<SystemSetting>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.bySection = upsertSetting(state.bySection, action.payload.data);
        }
      })
      .addCase(thunks.createSystemSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.updateSystemSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.updateSystemSetting.fulfilled, (state, action: PayloadAction<APIResponse<SystemSetting>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.bySection = upsertSetting(state.bySection, action.payload.data);
        }
        if (state.selected && state.selected.id === action.payload.data?.id) {
          state.selected = action.payload.data;
        }
      })
      .addCase(thunks.updateSystemSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.deleteSystemSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.deleteSystemSetting.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        const deletedSettingId = action.payload;
        const deletedSetting = flattenSettingsMap(state.bySection).find((item) => item.id === deletedSettingId);
        if (deletedSetting) {
          state.bySection = removeSetting(state.bySection, deletedSetting);
        }
        if (state.selected && state.selected.id === deletedSettingId) {
          state.selected = null;
        }
      })
      .addCase(thunks.deleteSystemSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      });
  },
});

export const selectSystemSettingsMap = (state: RootState) => state.systemSettings.bySection;
export const selectSystemSettings = (state: RootState) => flattenSettingsMap(state.systemSettings.bySection);
export const selectSystemSettingById = (state: RootState, id: number) =>
  flattenSettingsMap(state.systemSettings.bySection).find((item) => item.id === id);
export const selectSystemSettingsSection = (state: RootState, section: string) =>
  Object.values(state.systemSettings.bySection[section] ?? {});
export const selectSystemSetting = (state: RootState, section: string, key: string) =>
  state.systemSettings.bySection[section]?.[key] ?? null;
export const selectSystemSettingValue = (state: RootState, section: string, key: string) =>
  state.systemSettings.bySection[section]?.[key]?.value_text ?? null;
export const selectSystemSettingBoolean = (
  state: RootState,
  section: string,
  key: string,
  fallback: boolean
) => {
  const setting = state.systemSettings.bySection[section]?.[key];
  if (!setting) return false;
  if (!setting.is_active) return false;
  const normalized = setting.value_text?.trim().toLowerCase();
  if (normalized === "true") return true;
  if (normalized === "false") return false;
  return fallback;
};
export const selectSystemSettingNumber = (
  state: RootState,
  section: string,
  key: string,
  fallback: number
) => {
  const setting = state.systemSettings.bySection[section]?.[key];
  const rawValue = setting?.value_text?.trim();
  if (!rawValue) return fallback;
  const parsed = Number(rawValue);
  if (Number.isFinite(parsed)) return parsed;
  return fallback;
};
export const selectSelectedSystemSetting = (state: RootState) => state.systemSettings.selected;
export const selectSystemSettingsLoading = (state: RootState) => state.systemSettings.loading;
export const selectSystemSettingsError = (state: RootState) => state.systemSettings.error;

export const {
  clearSystemSettings,
  replaceSystemSettings,
  clearSelectedSystemSetting,
  clearSystemSettingsError,
} = systemSettingsSlice.actions;

export default systemSettingsSlice.reducer;
